import { BudgetEstimateComponent } from '@/components/shared/budget-estimate/budget-estimate.component';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  FileFieldProps,
  SelectFieldProps,
  SelectOption,
  TextFieldProps,
  DateFieldProps,
} from '../../../../components/form-builder/form-builder.types';
import { MultiFormAccordionComponent } from '../../../../components/multi-form-accordion/multi-form-accordion.component';
import { PpmRepository } from '../../../../store/ppm-state/ppm-state.repository';
import { FileRemoveEvent, FileSelectEvent } from 'primeng/fileupload';
import { ResponseDoc } from '@/types';
import { map, of, Observable } from 'rxjs';
import { PpmMilestoneFormComponent } from '../../milestone-form/milestone-form.component';

interface YearFieldConfig {
  minDate?: Date;
  maxDate?: Date;
  allowedYears?: number[];
  isDisabled?: Observable<boolean>;
}

interface getPpmNewPlanFormObjectProps {
  store: PpmRepository;
  acOptions: SelectOption[];
  clearDirection?: () => void;
  minDate?: Date;
  yearConfig?: YearFieldConfig;
  onAcChange?: (acId?: number) => void;
}

export const getPpmNewPlanFormObject = ({
  store,
  acOptions,
  clearDirection,
  minDate,
  yearConfig,
  onAcChange,
}: getPpmNewPlanFormObjectProps): DynamicForm => {
  const acOptionsField: DynamicField<SelectFieldProps> = {
    label: 'ppm.form.ac_options',
    variant: FieldVariant.SELECT,
    isDisabled: of(acOptions.length === 1),
    error: store.getNestedObservable<string>('errors', 'acId'),
    props: {
      value: store.getObservable('acsSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('acsSelected', value);
        store.setNested('dto', 'acId', value?.code as number);
        store.clearError('acId');
        store.clearError('annee');
        clearDirection?.();
        onAcChange?.(value?.code as number);
      },
      placeholder: 'ppm.form.placeholders.ac_options',
      options: acOptions,
      filterEnabled: true,
    },
  };

  const yearField: DynamicField<DateFieldProps> = {
    label: 'ppm.form.year',
    variant: FieldVariant.DATE,
    isRequired: true,
    isDisabled: yearConfig?.isDisabled ?? of(true),
    error: store.getNestedObservable<string>('errors', 'annee'),
    props: {
      value: store.getNestedObservable<number>('dto', 'annee'),
      onChange: (value: Date) => {
        if (!value) {
          store.setNested('dto', 'annee', undefined);
          return;
        }
        const year = value.getFullYear();
        if (yearConfig?.allowedYears?.length && !yearConfig.allowedYears.includes(year)) return;
        store.setNested('dto', 'annee', year.toString());
        store.clearError('annee');
      },
      view: 'year',
      format: 'yy',
      minDate: yearConfig?.minDate,
      lastDate: yearConfig?.maxDate,
    },
  };

  const nameField: DynamicField<TextFieldProps> = {
    label: 'ppm.form.name',
    variant: FieldVariant.TEXT,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dto', 'nom'),
      onChange: (value: string) => {
        store.setNested('dto', 'nom', value);
      },
      placeholder: 'ppm.form.placeholders.sysName',
    },
  };

  const milestoneField: DynamicField<CustomFieldProps> = {
    label: 'ppm.form.milestone',
    variant: FieldVariant.CUSTOM,
    props: {
      component: MultiFormAccordionComponent,
      injectKeys: ['accordionObject'],
      accordionObject: {
        titles: ['ppm.form.milestone2'],
        component: PpmMilestoneFormComponent,
        injectKeys: ['acId', 'actualizedCount'],
        acId: store.getNestedObservable<number>('dto', 'acId'),
        actualizedCount: of(0),
        minUpdateDate: minDate,
        downSyndromeComponent: BudgetEstimateComponent,
        downSyndromeProps: {
          label: 'milestone.form.estimated_total_budget',
          value: store.getObservable<number>('totalEstimatedBudget'),
        },
      },
    },
  };

  const uploadField: DynamicField<FileFieldProps> = {
    label: 'ppm.form.files',
    variant: FieldVariant.FILE,
    props: {
      mode: 'advanced',
      accept: 'image/*, application/pdf',
      multiple: true,
      value: store.getNestedObservable<File[]>('dto', 'files'),
      onFileSelect: (event: FileSelectEvent) => {
        store.setNested('dto', 'ppmFiles', event.currentFiles);
      },
      onFileRemoved: (event: FileRemoveEvent) => {
        store.setNested(
          'dto',
          'ppmFiles',
          store.getNested<File[]>('dto', 'ppmFiles').filter(file => file.name !== event.file.name)
        );
      },
      files: store
        .getNestedObservable<ResponseDoc[]>('response', 'docs')
        .pipe(
          map(docs =>
            docs.map(doc => ({ id: doc.id, filename: doc.name, extension: doc.fileExtension, size: doc.fileSize, alfresco_id: doc.alfresco_id }))
          )
        ),
      onFileRepresentationChange: (file: ResponseDoc) => {
        store.setNested(
          'response',
          'docs',
          store.getNested<ResponseDoc[]>('response', 'docs').filter(doc => doc.id != file.id)
        );
        store.setNested(
          'dto',
          'docIds',
          store.getNested<number[]>('dto', 'docIds').filter(docId => docId !== file.id)
        );
      },
      // onFileDownLoad: (file: FileRepresentation) => download?.(file),
      // openFile: (file: FileRepresentation) => openFile?.(file),
    },
  };

  const notesField: DynamicField<EditorFieldProps> = {
    label: 'ppm.form.remarks',
    variant: FieldVariant.EDITOR,
    props: {
      height: '320px',
      value: store.getNestedObservable<string>('dto', 'remarque'),
      onChange: (value: string) => {
        store.setNested('dto', 'remarque', value);
        store.clearError('remarque');
      },
    },
  };

  return {
    title: 'Plan annuel des achats de cette année',
    description: '',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: 'Information Avis',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [acOptionsField],
          },
          {
            fields: [yearField, nameField],
          },
        ],
      },
      {
        title: 'Réalisation envisagée',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [milestoneField],
          },
        ],
      },
      {
        title: 'Entrée secondaire',
        gridItems: [
          {
            fields: [uploadField],
          },
          {
            fields: [notesField],
          },
        ],
      },
    ],
  };
};
